import React, { useEffect, useState, useRef } from 'react';
import { useForm, FormProvider, useFormContext } from 'react-hook-form';
import { formatDistanceToNow } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import OptionToRevisitStudiesModal from '../OptionToRevisitStudiesModal';
import {
  CurrentReviewerProvider,
  useCurrentReviewerContext,
} from '../../../contexts/CurrentReviewerProvider';
import { QueryProvider } from '../../../query/QueryProvider';
import {
  UnchangedDefaultsWarningModal,
  showUnchangedDefaultWarning,
} from './components/Modals/UnchangedDefaultsWarningModal';
import InterventionsPanel from './components/InterventionsPanel/InterventionsPanel';
import PopulationPanel from './components/PopulationPanel/PopulationPanel';
import OutcomesPanel from './components/OutcomesPanel/OutcomesPanel';
import IdentificationPanel from './components/IdentificationPanel/IdentificationPanel';
import MethodsPanel from './components/MethodsPanel/MethodsPanel';
import * as styles from './DataExtractionTemplate.module.scss';
import {
  InterventionWizardProvider,
  useInterventionWizardContext,
} from './components/InterventionsPanel/InterventionWizardProvider';
import {
  OutcomeWizardProvider,
  useOutcomeWizardContext,
} from './components/OutcomesPanel/OutcomeWizardProvider';
import { TemplateSection } from './components/TemplateSection';
import Wizard from './components/Wizard/Wizard';
import {
  interventionsPendoKeys,
  InterventionsWizardConfig,
} from './components/InterventionsPanel/InterventionsWizardConfig';
import WelcomePanel from './components/WelcomePanel/WelcomePanel';
import OutcomeWizard from './components/OutcomesPanel/OutcomeWizard';
import { useCustomMeasureWarningDialog } from './hooks/useCustomMeasureWarningDialog';
import { scrollToSection, showErrors } from './utils';
import { useConfirmUnsaved } from 'hooks/useConfirmUnsaved';
import { DataExtractionTemplate as DataExtractionTemplateType } from 'types/DataExtraction';
import {
  Button,
  Icon,
  IconList,
  Link,
  Text,
  createToast,
  getFocusedElement,
  Toast,
  Accordion,
  MenuButton,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuTrigger,
} from 'components/core';
import { ReviewFeaturesProvider } from 'contexts/ReviewFeaturesProvider';
import { IsolationPage, AppFooter, TemplateToolInfo } from 'components/shared';
import {
  EXTRACT_DATA_ROUTE,
  EXTRACTION_STUDIES_LIST_ROUTE,
  tokenizeRoute,
  getChooseYourTemplateBackRoute,
} from 'query/routes';
import {
  getDETemplate,
  updateDETemplate,
  createEmptyDETemplate,
  getReviewId,
  getHttpErrorMessage,
  UpdateResponseBody,
} from 'query/review';
import { GrooveManager } from 'components/shared/PrimaryNavigation';
import { useReviewFeatures } from 'hooks/reviews/useReviewFeatures';
import { useCurrentReviewer } from 'hooks/reviewers/useCurrentReviewer';
import { studyListCookies } from 'pages/StudyList/studyListCookies';
import useCookie from 'hooks/useCookie';

// copy this here so we can add to it if needed
export type DataExtractionTemplateState = DataExtractionTemplateType;

type Props = {
  showGroove?: boolean;
  getTemplate?: () => Promise<DataExtractionTemplateType>;
  updateTemplate?: (
    template: DataExtractionTemplateType,
    revisitStudies: boolean
  ) => Promise<{ status: number; body: UpdateResponseBody }>;
};

const useRoutes = (fromStudy: string | null) => {
  const [urlParams] = useSearchParams();
  const backToParam = urlParams.get('backTo');
  const typeParam = urlParams.get('type') as 'de' | 'qa' | null;
  const skipSelectorParam = urlParams.get('skipSelector') === 'true';
  const backRoute = fromStudy
    ? tokenizeRoute(EXTRACT_DATA_ROUTE, {
        review_id: getReviewId(),
        study_id: fromStudy,
      })
    : backToParam
    ? getChooseYourTemplateBackRoute({
        reviewId: getReviewId(),
        type: typeParam ?? 'de',
        backTo: backToParam,
        skipSelector: skipSelectorParam,
      })
    : tokenizeRoute(EXTRACTION_STUDIES_LIST_ROUTE, {
        review_id: getReviewId(),
      });

  const publishRoute = fromStudy
    ? tokenizeRoute(EXTRACT_DATA_ROUTE, {
        review_id: getReviewId(),
        study_id: fromStudy,
      })
    : tokenizeRoute(EXTRACTION_STUDIES_LIST_ROUTE, {
        review_id: getReviewId(),
      });

  return { backRoute, publishRoute };
};

const DataExtractionTemplate = (props: Props) => {
  const formContext = useForm<DataExtractionTemplateState>({
    mode: 'all',
    defaultValues: createEmptyDETemplate(),
    shouldFocusError: false,
  });

  return (
    <FormProvider {...formContext}>
      <QueryProvider>
        <CurrentReviewerProvider>
          <ReviewFeaturesProvider>
            <InterventionWizardProvider>
              <OutcomeWizardProvider>
                <DataExtractionTemplateBody {...props} />
              </OutcomeWizardProvider>
            </InterventionWizardProvider>
          </ReviewFeaturesProvider>
        </CurrentReviewerProvider>
      </QueryProvider>
    </FormProvider>
  );
};

const useTemplate = (args: {
  getTemplate: Props['getTemplate'];
  isCopyingTemplate: boolean;
  reset: (arg: any) => void;
}) => {
  const { getTemplate, isCopyingTemplate, reset } = args;
  const [isLoading, setLoading] = useState(true);
  const [template, setTemplate] = useState<DataExtractionTemplateType>();
  useEffect(() => {
    getTemplate &&
      getTemplate()
        .then((template) => {
          setTemplate(template);

          if (isCopyingTemplate) {
            createToast(
              <Toast type="success">Your template has been copied</Toast>
            );
          }
        })
        .catch(() => {
          createToast(
            <Toast type="danger">Something went wrong, please try again</Toast>
          );
        });
  }, []);

  useEffect(() => {
    reset(template);
    setLoading(false);
  }, [reset, template]);

  return { template, isLoading, setLoading };
};

const DataExtractionTemplateBody = ({
  showGroove = true,
  getTemplate = getDETemplate,
  updateTemplate = updateDETemplate,
}: Props) => {
  const [urlParams] = useSearchParams();
  const fromStudy = urlParams.get('studyId');

  const { backRoute, publishRoute } = useRoutes(fromStudy);

  const isCopyingTemplate = !!urlParams.get('source_review_id');
  const { isLoading: currentReviewerIsLoading } = useCurrentReviewerContext();
  const { useSuggestions } = useReviewFeatures();
  const { currentReviewer } = useCurrentReviewer();

  const { getValues } = useFormContext();

  const publishButtonText = fromStudy
    ? 'Publish & return to study'
    : 'Publish template';

  const [, saveDePublishCookie] = useCookie<boolean>(
    studyListCookies.deTemplatePublished,
    false
  );
  const [, saveMoveBackCookie] = useCookie<boolean>(
    studyListCookies.deMovedBack,
    false
  );
  const [modal, setModal] = useState<React.ReactElement | null>();
  const revisitStudies = useRef(false);
  const formContext = useFormContext<
    DataExtractionTemplateType,
    any,
    undefined
  >();

  const {
    reset,
    handleSubmit,
    formState: { isDirty, isValid },
  } = formContext;

  const [needHelpIcon, setNeedHelpIcon] = useState<IconDefinition>(
    IconList.light.faChevronDown
  );
  const [clean, dirty] = useConfirmUnsaved();
  useEffect(() => {
    if (isCopyingTemplate) {
      dirty();
      return;
    }

    isDirty ? dirty() : clean();
  }, [isCopyingTemplate, isDirty, dirty, clean]);

  const { template, isLoading, setLoading } = useTemplate({
    getTemplate,
    isCopyingTemplate,
    reset,
  });

  // Init Groove live chat (DET variant) if enabled
  useEffect(() => {
    if (showGroove) {
      GrooveManager.getInstance(true).init();
    }
  }, [showGroove]);

  const shouldShowRevisitModal = (): boolean => {
    return (
      (template?.number_of_revisitable_studies || 0) > 0 && isValid && isDirty
    );
  };

  const onPublish = (revisit = false) => {
    revisitStudies.current = revisit;
    publishTemplate();
  };

  const publishTemplate = handleSubmit(
    (template) => {
      // Captured up front: submission can come from the Publish button or
      // from pressing Enter in a field, so there's no single button ref to
      // rely on - and by the time the save resolves, the Publish button
      // (disabled while submitting) will have already lost focus.
      const triggerElement = getFocusedElement();

      setLoading(true);
      const revisiting = revisitStudies.current;
      updateTemplate(template, revisiting)
        .then(({ status, body }) => {
          if (status === 200) {
            clean();
            window.location.href = publishRoute.toString();
            saveDePublishCookie(true, 'session');
            if (revisiting) {
              saveMoveBackCookie(true, 'session');
            }
            return;
          }

          const errorMessages = body.errors || [getHttpErrorMessage(status)];
          showErrors(errorMessages, true, triggerElement);
        })
        .finally(() => setLoading(false));
    },
    () => {
      const invalidInput = document.querySelector(
        '[aria-invalid=true]'
      ) as HTMLElement;
      invalidInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => invalidInput?.focus(), 1000);
    }
  );

  const showOptionToRevisitModal = () => {
    setModal(
      <OptionToRevisitStudiesModal
        numberOfRevisitableStudies={
          template?.number_of_revisitable_studies || 0
        }
        onClose={() => setModal(null)}
        onPublish={onPublish}
        templateType="DataExtraction"
      />
    );
  };

  const showUnchangedDefaultsWarningModal = () => {
    setModal(
      <UnchangedDefaultsWarningModal
        onClose={() => setModal(null)}
        onPublish={onPublish}
      />
    );
  };

  const interventionWizard = useInterventionWizardContext();
  const outcomeWizard = useOutcomeWizardContext();

  const onNeedHelpMenuOpenChange = (isOpen: boolean) => {
    setNeedHelpIcon(
      isOpen ? IconList.light.faChevronUp : IconList.light.faChevronDown
    );
  };

  const handlePublish = () => {
    if (interventionWizard.shouldForceWizardUse) {
      showErrors(['You must set up interventions before publishing'], false);
      scrollToSection('4._interventions');
    } else if (outcomeWizard.shouldForceWizardUse) {
      showErrors(['You must set up outcomes before publishing'], false);
      scrollToSection('5._outcomes');
    } else if (shouldShowRevisitModal()) {
      showOptionToRevisitModal();
    } else if (showUnchangedDefaultWarning(formContext)) {
      showUnchangedDefaultsWarningModal();
    } else {
      publishTemplate();
    }
  };

  const resultsDataAnchor = 'resultsdata';

  // Show warning dialog if custom measure labels were automatically changed
  useCustomMeasureWarningDialog(template, setModal);

  if (currentReviewerIsLoading) return null; // Prevent render flicker on load

  const supportedSuggestions = template?.custom_field_suggestions || [];

  return (
    <>
      {modal}
      <IsolationPage
        back={backRoute}
        title={
          <TemplateToolInfo
            title="Data extraction template"
            subTitle="Extraction 1"
            icon={IconList.light.faTableList}
          />
        }
        controls={
          <>
            {template?.updated_at && (
              <Text variant="weak">
                Published{' '}
                {formatDistanceToNow(Date.parse(template?.updated_at), {
                  addSuffix: true,
                })}{' '}
                by {template?.updated_by?.first_name}
              </Text>
            )}

            <MenuTrigger onMenuOpenChange={onNeedHelpMenuOpenChange}>
              <MenuButton type="brand" variant="outline">
                Need help?
                <Icon icon={needHelpIcon} />
              </MenuButton>
              <MenuPopover
                placement="bottom start"
                className={styles.NeedHelpPopover}
              >
                <MenuList>
                  <MenuItem
                    href="https://support.covidence.org/help/how-to-create-a-data-extraction-template-in-extraction-1"
                    target="_blank"
                  >
                    <Text>
                      <Icon
                        icon={IconList.light.faFileLines}
                        className={styles.HelpIcon}
                      />
                      Read the step-by-step guide
                    </Text>
                    <Icon
                      icon={IconList.light.faArrowUpRightFromSquare}
                      className={styles.HelpOpenIcon}
                    />
                  </MenuItem>
                  <MenuItem
                    href="https://www.youtube.com/watch?v=8CGe1QJfAfg"
                    target="_blank"
                  >
                    <Text>
                      <Icon
                        icon={IconList.light.faClapperboardPlay}
                        className={styles.HelpIcon}
                      />
                      Watch tutorial video
                    </Text>
                    <Icon
                      icon={IconList.light.faArrowUpRightFromSquare}
                      className={styles.HelpOpenIcon}
                    />
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </MenuTrigger>
            <Button
              onClick={handlePublish}
              type="brand"
              disabled={isLoading}
              isLoading={isLoading}
              data-pendo-key="det-publish"
            >
              {publishButtonText}
            </Button>
            <Link
              className={styles.HelpLink}
              onClick={() => ''}
              data-pendo-key="det-help"
              aria-label="Get help"
            >
              <Icon icon={IconList.light.faQuestionCircle} />
            </Link>
          </>
        }
      >
        <Accordion>
          <WelcomePanel
            useSuggestions={useSuggestions}
            reviewerFirstName={
              currentReviewer?.first_name
                ? `${currentReviewer.first_name} `
                : ''
            }
            isNewTemplate={isCopyingTemplate || !getValues('updated_by')}
          />
          <TemplateSection
            title="1. Identification"
            isInitiallyOpen={true}
            subTitle="What general study details do you want to collect?"
            pendoKey="det-identification"
            ariaLabel="Get help with identification"
          >
            <IdentificationPanel
              useSuggestions={useSuggestions}
              hasExtractedData={template?.has_extracted_data}
              supportedSuggestions={supportedSuggestions}
            />
          </TemplateSection>
          <TemplateSection
            title="2. Methods"
            isInitiallyOpen={isCopyingTemplate}
            subTitle="What details about how the study was conducted do you want to collect?"
            pendoKey="det-methods"
            ariaLabel="Get help with methods"
          >
            <MethodsPanel
              useSuggestions={useSuggestions}
              hasExtractedData={template?.has_extracted_data}
              supportedSuggestions={supportedSuggestions}
            />
          </TemplateSection>
          <TemplateSection
            title="3. Population"
            isInitiallyOpen={isCopyingTemplate}
            subTitle="What information do you want to collect about the participants in the study?"
            pendoKey="det-population"
            ariaLabel="Get help with population"
          >
            <PopulationPanel
              useSuggestions={useSuggestions}
              hasExtractedData={template?.has_extracted_data}
              supportedSuggestions={supportedSuggestions}
            />
          </TemplateSection>
          <TemplateSection
            title="4. Interventions"
            isInitiallyOpen={isCopyingTemplate}
            subTitle="What interventions or exposures do you plan to compare?"
            wizard={interventionWizard}
            pendoKey="det-interventions"
            ariaLabel="Get help with interventions"
          >
            <InterventionsPanel
              hasExtractedData={template?.has_extracted_data}
              useSuggestions={useSuggestions}
            />
          </TemplateSection>
          <TemplateSection
            title="5. Outcomes"
            isInitiallyOpen={isCopyingTemplate}
            subTitle="What outcomes do you want to collect to assess the impact and effectiveness of interventions?"
            wizard={outcomeWizard}
            pendoKey="det-outcomes"
            ariaLabel="Get help with outcomes"
          >
            <OutcomesPanel resultsDataAnchor={resultsDataAnchor} />
          </TemplateSection>
        </Accordion>
        <Wizard
          id="interventions-wizard"
          data={InterventionsWizardConfig({
            hasExtractedData: template?.has_extracted_data,
          })}
          pendoKey={interventionsPendoKeys}
          isOpen={interventionWizard.isWizardOpen}
          onClose={() => {
            interventionWizard.setWizardHasBeenDisplayed(true);
            interventionWizard.setWizardOpen(false);
          }}
        />
        <OutcomeWizard />
        <AppFooter />
      </IsolationPage>
    </>
  );
};

export default DataExtractionTemplate;
